"use client";

import { useEffect, useRef } from "react";
import MediaImage from "@/components/ui/MediaImage";
import {
  attachBeforeAfter,
  resolveBeforeAfter,
  valueText,
  type BeforeAfterConfig,
} from "@/utils/before-after";

/** Two photos of the same place with a divider between them (look system only); src/styles/before-after.css draws it. */
const BeforeAfterComponent: React.FC<{
  config: BeforeAfterConfig & { lookSystem?: boolean };
  /** Said after the handle's name, so a screen reader tells several sliders apart (Før og efter's comparison title). */
  name?: string;
  loading?: "lazy" | "eager";
}> = ({ config, name, loading }) => {
  const figure = useRef<HTMLElement>(null);
  const slider = config.lookSystem ? resolveBeforeAfter(config) : undefined;
  const start = slider?.start;
  const beforeLabel = slider?.beforeLabel;
  const afterLabel = slider?.afterLabel;
  useEffect(() => {
    if (!figure.current || start === undefined) return;
    return attachBeforeAfter(figure.current, {
      start,
      beforeLabel: beforeLabel!,
      afterLabel: afterLabel!,
    });
  }, [start, beforeLabel, afterLabel]);
  if (!slider) return null;
  return (
    <figure
      ref={figure}
      data-re-before-after=""
      style={
        {
          "--re-before-after-position": `${slider.start}%`,
          "--re-before-after-ratio": slider.ratio,
          "--re-before-after-ratio-desktop": slider.ratioDesktop,
        } as React.CSSProperties
      }
    >
      <MediaImage
        data-re-before-after-image="after"
        src={slider.afterUrl}
        alt={slider.afterLabel}
        legacyAlt={slider.afterAlt}
        loading={loading}
        draggable={false}
      />
      <div data-re-before-after-before="">
        <MediaImage
          data-re-before-after-image="before"
          src={slider.beforeUrl}
          alt={slider.beforeLabel}
          legacyAlt={slider.beforeAlt}
          loading={loading}
          draggable={false}
        />
      </div>
      <span data-re-before-after-label="before">{slider.beforeLabel}</span>
      <span data-re-before-after-label="after">{slider.afterLabel}</span>
      <span data-re-before-after-divider="" aria-hidden="true" />
      <span
        data-re-before-after-handle=""
        role="slider"
        tabIndex={0}
        aria-label={`Skillelinje mellem ${slider.beforeLabel} og ${slider.afterLabel}${name ? `: ${name}` : ""}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(slider.start)}
        aria-valuetext={valueText(
          slider.start,
          slider.beforeLabel,
          slider.afterLabel,
        )}
        aria-orientation="horizontal"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            d="M9 6l-6 6 6 6M15 6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </figure>
  );
};

export default BeforeAfterComponent;
